/**
 * Settings → Workflow Configuration → **Workflow Engine** (ADR-0011).
 *
 * The all-module catalog — the LIST view the nav entry lands on (standing
 * rule 10). The workflow engine is a PLATFORM service, not a Sample
 * Preparation feature: Sample Prep is one module of five. The graph model is
 * identical throughout; what changes per module is the SCOPE KEY that selects
 * which workflow applies, the legal component palette, and the unit of work.
 *
 * Layout: MODULE SIDE-NAVIGATION on the left (built dynamically from the data —
 * see below), fuzzy search directly above the grid, then the shared grid.
 *
 * Standing rules honoured here:
 *   · 10  — navigation lands on this list; the designer opens from a row
 *   · 7/9 — shared <AppDataGrid> / <AppToolbar> / <FormTitle> / <GridSearch> /
 *           useNotify / useDeleteFlow; no per-form grid styling, no bespoke toast
 *   · 11  — the audit log surfaces automatically via <FormTitle auditFormName>,
 *           gated by Settings → Audit Trail Configuration
 *   · §14b.6 — dates only ever through the shared fmtLocal* helpers
 *
 * The designer canvas is the next phase; Edit reports that for now. Rows are
 * live data from dbo.AI_Workflow via /api/v1/workflow-config.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { GridSearch } from '../../components/GridSearch';
import { useNotify } from '../../components/notifications';
import { useDeleteFlow } from '../../components/confirm';
import { deleteWorkflow, listWorkflows } from './api';
import WorkflowBuilderPage from './WorkflowBuilderPage';
import {
  WORKFLOW_MODULES, scopeOf, type WorkflowListRow, type WorkflowModuleId,
} from './types';
import '../backlog/backlog.css';
import './workflow.css';

type ModuleFilter = WorkflowModuleId | 'all';

/** Current tree selection: a module, optionally narrowed to one sub-group. */
interface TreeSel { module: ModuleFilter; sub: string | null }

/** A module branch in the navigation tree, with its sub-groups. */
interface ModuleNode {
  id: ModuleFilter;
  label: string;
  icon: string;
  scopeLabel: string;
  /** Entry variants — module metadata, shown here rather than repeated as a
   *  grid column on every row of that module. */
  variants: string[];
  count: number;
  children: { key: string; label: string; count: number }[];
}

const UNASSIGNED = '— Unassigned';

export default function WorkflowConfigListPage() {
  const notify = useNotify();
  const runDelete = useDeleteFlow();

  const [rows, setRows]         = useState<WorkflowListRow[]>([]);
  const [loading, setLoading]   = useState(true);
  const [loadMs, setLoadMs]     = useState<number | null>(null);
  const [sel, setSel]           = useState<TreeSel>({ module: 'all', sub: null });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  /** Non-null = the full-page builder has REPLACED the list (rule 12). */
  const [editing, setEditing]   = useState<{ oid: string | null; moduleId: WorkflowModuleId; mode: 'view' | 'edit' } | null>(null);
  const searchWrapRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const started = performance.now();
    try {
      setRows(await listWorkflows());
    } catch {
      // The shared axios client already surfaced the error; show an empty grid
      // rather than stale rows from the previous load.
      setRows([]);
    } finally {
      setLoadMs(Math.round(performance.now() - started));
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  /**
   * The module side-nav is built DYNAMICALLY from the modules actually present
   * in the data — not from a hardcoded list. A sixth module added to
   * dbo.AI_Workflow appears here on the next refresh with no UI change, which
   * is the same "adding a module is a data change" property the engine has
   * everywhere else (ADR-0011 §3).
   *
   * WORKFLOW_MODULES supplies only the presentation (label, icon, scope key);
   * an id with no metadata still lists, under its raw id, rather than vanishing.
   */
  const moduleNav = useMemo<ModuleNode[]>(() => {
    const byModule = new Map<string, Map<string, number>>();
    for (const r of rows) {
      const subs = byModule.get(r.moduleId) ?? new Map<string, number>();
      const key = r.subGroup?.trim() || UNASSIGNED;
      subs.set(key, (subs.get(key) ?? 0) + 1);
      byModule.set(r.moduleId, subs);
    }

    const items: ModuleNode[] = [...byModule.entries()].map(([id, subs]) => {
      const meta = WORKFLOW_MODULES[id as WorkflowModuleId];
      const children = [...subs.entries()]
        .map(([key, count]) => ({ key, label: key, count }))
        // "Unassigned" always sorts last so real sub-groups lead.
        .sort((a, b) =>
          a.key === UNASSIGNED ? 1 : b.key === UNASSIGNED ? -1 : a.label.localeCompare(b.label));

      return {
        id: id as ModuleFilter,
        label: meta?.label ?? id,
        icon: meta?.icon ?? 'ti-sitemap',
        scopeLabel: meta?.scopeLabel ?? '—',
        variants: meta?.variants ?? [],
        count: [...subs.values()].reduce((a, b) => a + b, 0),
        children,
      };
    });
    items.sort((a, b) => a.label.localeCompare(b.label));

    return [
      { id: 'all', label: 'All Modules', icon: 'ti-layout-grid', scopeLabel: '',
        variants: [], count: rows.length, children: [] },
      ...items,
    ];
  }, [rows]);

  /** Expand the selected module by default, so a first click reveals its
   *  sub-groups without a second one. */
  useEffect(() => {
    if (sel.module !== 'all') setExpanded(prev => new Set(prev).add(sel.module));
  }, [sel.module]);

  const toggleExpand = useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  /** Module filter first, then the fuzzy search across every visible field. */
  const visible = useMemo(() => {
    let scoped = sel.module === 'all' ? rows : rows.filter(r => r.moduleId === sel.module);
    if (sel.sub) scoped = scoped.filter(r => (r.subGroup?.trim() || UNASSIGNED) === sel.sub);
    const byModule = scoped;
    const q = search.trim().toLowerCase();
    if (!q) return byModule;

    // Fuzzy across everything the grid shows, so "200.7", "pfas", "draft" and
    // "MET-ICPOES-W" all find their row without the user choosing a column.
    return byModule.filter(r => [
      WORKFLOW_MODULES[r.moduleId]?.label ?? r.moduleId,
      r.workflowName,
      r.description ?? '',
      scopeOf(r),
      (WORKFLOW_MODULES[r.moduleId]?.variants ?? []).join(' '),
      r.status,
      r.workflowCode ?? '',
      r.subGroup ?? '',
      String(r.version),
    ].join(' ').toLowerCase().includes(q));
  }, [rows, sel, search]);

  const selectedRow = useMemo(
    () => visible.find(r => r.oid === selected) ?? null,
    [visible, selected],
  );

  /**
   * FIVE columns, in the vocabulary of the reviewed designer
   * (Flow ID · Flow Name · Flow Type · …).
   *
   * Three were cut on 2026-08-14 because they repeated something already on
   * screen rather than telling you anything about the row:
   *   · EntryVariants — module metadata, identical on every row of a module.
   *     It now sits on the module in the side-navigation, where it varies.
   *   · Nodes / Ver    — detail, not identity. Both are on the record itself.
   *   · the ApplyTo sub-caption ("Test × Matrix × Method") — the same phrase on
   *     every row of a module, and already shown under the module in the tree.
   *
   * FlowType only earns its place on "All Modules"; once the tree is filtered
   * every row repeats it.
   */
  const columns = useMemo<AppColumn<WorkflowListRow>[]>(() => [
    { field: 'flowId', header: 'FlowID', width: 150,
      value: r => r.workflowCode ?? r.oid },
    {
      field: 'flowName', header: 'FlowName', width: 260,
      value: r => r.workflowName,
      render: r => (
        <span>
          <b>{r.workflowName}</b>
          {r.description ? <div className="wf-sub">{r.description}</div> : null}
        </span>
      ),
    },
    ...(sel.module === 'all' ? [{
      field: 'flowType', header: 'FlowType', width: 170,
      value: (r: WorkflowListRow) => WORKFLOW_MODULES[r.moduleId]?.label ?? r.moduleId,
      render: (r: WorkflowListRow) => {
        const m = WORKFLOW_MODULES[r.moduleId];
        return <span>{m ? <i className={`ti ${m.icon}`} /> : null} {m?.label ?? r.moduleId}</span>;
      },
    } as AppColumn<WorkflowListRow>] : []),
    {
      // What SELECTS this workflow. Its meaning differs per module — prep is
      // Matrix × Test × Method, analysis is Test × Method — and scopeOf() is
      // the single place that decision lives, so the grid, the side-nav and the
      // builder can never disagree.
      field: 'appliesTo', header: 'Applies To', width: 280,
      value: r => scopeOf(r),
    },
    { field: 'status', header: 'Status', type: 'status', width: 110, value: r => r.status },
  ], [sel.module]);

  /** List → detail is the ONLY flow (standing rules 10 & 12): opening a
   *  workflow REPLACES the list with the full-page builder.
   *
   *  Double-click opens READ-ONLY so a published configuration cannot be
   *  altered just by looking at it; the toolbar Edit button is the deliberate
   *  act that opens it editable. */
  const openBuilder = useCallback((row: WorkflowListRow | null, mode: 'view' | 'edit' = 'view') => {
    if (!row) return;
    setEditing({ oid: row.oid, moduleId: row.moduleId, mode });
  }, []);

  const openNew = useCallback(() => {
    if (sel.module === 'all') {
      notify.info('Pick a module on the left first — the module decides which components the workflow can use.');
      return;
    }
    setEditing({ oid: null, moduleId: sel.module as WorkflowModuleId, mode: 'edit' });
  }, [sel.module, notify]);

  const onDelete = useCallback(() => {
    if (!selectedRow) return;
    void runDelete({
      formName: 'Workflow Configuration',
      message: `Delete the workflow “${selectedRow.workflowName}”? Its components and connections are removed with it.`,
      delete: async () => { await deleteWorkflow(selectedRow.oid); },
      onDeleted: () => { setSelected(null); void load(); },
    });
  }, [selectedRow, runDelete, load]);

  // List → detail: the builder REPLACES the list, it is not a popup (rule 12).
  if (editing) {
    return (
      <WorkflowBuilderPage
        oid={editing.oid}
        moduleId={editing.moduleId}
        initialMode={editing.mode}
        siblingIds={visible.map(r => r.oid)}
        onNavigate={next => {
          const row = visible.find(r => r.oid === next);
          if (row) setEditing({ oid: row.oid, moduleId: row.moduleId, mode: editing.mode });
        }}
        onSavedNew={() => setEditing({ oid: null, moduleId: editing.moduleId, mode: 'edit' })}
        onClose={() => { setEditing(null); void load(); }}
      />
    );
  }

  return (
    <div className="bl-wrap">
      <FormTitle
        pageId="workflow-engine"
        loadMs={loadMs}
        loading={loading}
        auditFormName="Workflow Configuration"
      />

      <div className="wf-split">
        {/* ── Module side-navigation — built from the data, not hardcoded ──── */}
        <aside className="bl-card wf-side">
          <div className="wf-side-hdr">Modules</div>
          <div className="wf-side-list" role="tree">
            {moduleNav.map(m => {
              const open = expanded.has(String(m.id));
              const onModule = sel.module === m.id && sel.sub === null;
              return (
                <div key={m.id} role="treeitem" aria-expanded={m.children.length ? open : undefined}>
                  <div className={`wf-side-item${onModule ? ' active' : ''}`}>
                    {/* Twisty is its own hit target — expanding a branch must
                        not change the filter, and filtering must not collapse. */}
                    {m.children.length > 0 ? (
                      <button
                        type="button"
                        className="wf-twisty"
                        aria-label={open ? `Collapse ${m.label}` : `Expand ${m.label}`}
                        onClick={e => { e.stopPropagation(); toggleExpand(String(m.id)); }}
                      >
                        <i className={`ti ${open ? 'ti-chevron-down' : 'ti-chevron-right'}`} />
                      </button>
                    ) : <span className="wf-twisty wf-twisty-empty" />}

                    <button
                      type="button"
                      className="wf-side-btn"
                      title={m.variants.length
                        ? `${m.label} — selected by ${m.scopeLabel}. Entry variants: ${m.variants.join(' · ')}`
                        : m.label}
                      onClick={() => { setSel({ module: m.id, sub: null }); setSelected(null); }}
                    >
                      <i className={`ti ${m.icon}`} />
                      <span className="wf-side-label">
                        {m.label}
                        {m.scopeLabel ? <span className="wf-side-scope">{m.scopeLabel}</span> : null}
                      </span>
                      <span className="wf-side-count">({m.count})</span>
                    </button>
                  </div>

                  {open && m.children.map(c => (
                    <button
                      key={c.key}
                      type="button"
                      role="treeitem"
                      className={`wf-side-item wf-side-sub${
                        sel.module === m.id && sel.sub === c.key ? ' active' : ''}`}
                      onClick={() => { setSel({ module: m.id, sub: c.key }); setSelected(null); }}
                    >
                      <i className="ti ti-point" />
                      <span className="wf-side-label">{c.label}</span>
                      <span className="wf-side-count">({c.count})</span>
                    </button>
                  ))}
                </div>
              );
            })}
            {!loading && rows.length === 0 && (
              <div className="wf-side-none">No workflows configured yet.</div>
            )}
          </div>
        </aside>

        {/* ── Toolbar · fuzzy search · grid ────────────────────────────────── */}
        <section className="bl-card bl-card-flex" style={{ flex: 1, minWidth: 0 }}>
          <AppToolbar
            onNew={openNew}
            onEdit={() => openBuilder(selectedRow, 'edit')}
            editDisabled={!selectedRow}
            onDelete={onDelete}
            deleteDisabled={!selectedRow}
            onRefresh={() => void load()}
            refreshing={loading}
            /* Shared export matrix so ALL filtered rows export, not just the
               visible page — the one canonical export path (§18.6). */
            exportData={{ filename: 'workflow-configuration', ...gridExportMatrix(visible, columns) }}
          />

          {/* Fuzzy search in its own row directly above the grid (BO UI Standard),
              with the solid magnifier button joined to it — same affordance as
              Training Records. Filtering is LIVE as you type, so the button is a
              focus/refocus target rather than a submit; it exists because users
              expect a magnifier next to a search box. Added through GridSearch's
              own `right` slot so the shared component is not forked (rule 7). */}
          <div className="wf-search" ref={searchWrapRef}>
            <GridSearch
              value={search}
              onChange={setSearch}
              placeholder="Search workflows…"
              right={
                <button
                  type="button"
                  className="ca-btn ca-btn-solid wf-search-btn"
                  title="Fuzzy search across module, workflow, ApplyTo, entry variants, status and WorkflowID"
                  aria-label="Search workflows"
                  onClick={() => {
                    const input = searchWrapRef.current?.querySelector('input');
                    input?.focus();
                    input?.select();
                  }}
                >
                  <i className="ti ti-search" />
                </button>
              }
            />
          </div>

          <AppDataGrid<WorkflowListRow>
            rows={visible}
            columns={columns}
            getRowId={r => r.oid}
            loading={loading}
            loadMs={null}            /* load time renders at the TOP in FormTitle */
            selectedRowId={selected}
            onRowClick={r => setSelected(r.oid)}
            onRowDoubleClick={r => openBuilder(r, 'view')}
            emptyText={
              search.trim()
                ? `No workflow matches “${search.trim()}”.`
                : sel.module === 'all'
                  ? 'No workflows configured yet. Use New to build one.'
                  : `No ${WORKFLOW_MODULES[sel.module as WorkflowModuleId]?.label ?? sel.module}${
                      sel.sub ? ` · ${sel.sub}` : ''} workflows configured yet.`
            }
            persistKey="workflow-config-list"
          />
        </section>
      </div>
    </div>
  );
}
